import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Input, Tag, Typography } from 'antd';
import {
  CaretDownOutlined,
  CaretRightOutlined,
  CheckCircleFilled,
  DeleteOutlined,
  ExclamationCircleFilled,
  KeyOutlined,
  ScanOutlined,
  SoundFilled,
  SoundOutlined,
  SwapOutlined
} from '@ant-design/icons';
import CustomModal from '@/common/CustomModal';
import QrCamera from './QrCamera';
import { playSuccessFeedback, playDuplicateFeedback } from './feedback';
import styles from './QrScan.module.less';

const TOAST_AUTO_HIDE_MS = 1500;

const extractLabel = (decoded) => {
  try {
    const url = new URL(decoded);
    const segs = url.pathname.split('/').filter(Boolean);
    return segs[segs.length - 1] || decoded.trim();
  } catch {
    return decoded.trim();
  }
};

/**
 * Gold-standard "scan to build a filter" modal. No target list.
 *
 * Props:
 *   visible           bool
 *   onCancel          () => void
 *   onApply           (labels: string[]) => void
 *   title             string
 *   description       string  — short helper text under the title
 *   noun              { singular, plural }
 *   manualPlaceholder string
 *   applyButtonText   string  — default "Apply Filter"
 */
const QrScanCollectorModal = ({
  visible,
  onCancel,
  onApply,
  title = 'Scan to Filter',
  description,
  noun = { singular: 'item', plural: 'items' },
  manualPlaceholder = 'Type label manually',
  applyButtonText = 'Apply Filter'
}) => {
  const [scanned, setScanned] = useState([]);
  const [facingMode, setFacingMode] = useState('environment');
  const [muted, setMuted] = useState(false);
  const [toast, setToast] = useState(null);
  const [flash, setFlash] = useState(null);
  const [statusLine, setStatusLine] = useState(null);
  const [showList, setShowList] = useState(true); // collector is more useful expanded
  const [showManual, setShowManual] = useState(false);
  const [manualInput, setManualInput] = useState('');
  const [cameraOk, setCameraOk] = useState(true);

  const scannedRef = useRef(scanned);
  scannedRef.current = scanned;
  const mutedRef = useRef(muted);
  mutedRef.current = muted;
  const toastTimerRef = useRef(null);
  const flashTimerRef = useRef(null);

  useEffect(() => {
    if (visible) return;
    setScanned([]);
    setToast(null);
    setFlash(null);
    setStatusLine(null);
    setShowList(true);
    setShowManual(false);
    setManualInput('');
    setCameraOk(true);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
  }, [visible]);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    };
  }, []);

  const showFeedback = useCallback((kind, label) => {
    setFlash(kind);
    setToast({ kind, label });
    if (kind === 'success') playSuccessFeedback(mutedRef.current);
    else playDuplicateFeedback(mutedRef.current);

    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    flashTimerRef.current = setTimeout(() => setFlash(null), 500);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(
      () => setToast(null),
      TOAST_AUTO_HIDE_MS
    );
  }, []);

  const processLabel = useCallback(
    (rawLabel) => {
      const upper = String(rawLabel || '')
        .toUpperCase()
        .trim();
      if (!upper) return;

      const alreadyIn = scannedRef.current.includes(upper);
      if (alreadyIn) {
        setStatusLine({ kind: 'duplicate', label: upper });
        showFeedback('duplicate', upper);
        return;
      }
      setScanned((prev) => [...prev, upper]);
      setStatusLine({ kind: 'success', label: upper });
      showFeedback('success', upper);
    },
    [showFeedback]
  );

  const handleCameraDecode = useCallback(
    (decoded) => processLabel(extractLabel(decoded)),
    [processLabel]
  );

  const handleCameraError = useCallback(() => {
    setCameraOk(false);
    setShowManual(true);
  }, []);

  const handleManualSubmit = () => {
    const val = manualInput.trim();
    if (!val) return;
    processLabel(val);
    setManualInput('');
  };

  const switchCamera = () =>
    setFacingMode((m) => (m === 'environment' ? 'user' : 'environment'));

  const counterText = `${scanned.length} scanned`;

  return (
    <CustomModal
      title={title}
      visible={visible}
      onCancel={onCancel}
      customFooter={false}
      footer={null}
      width={520}
      destroyOnClose
    >
      {description && (
        <Typography.Text
          type="secondary"
          style={{ display: 'block', marginBottom: 12 }}
        >
          {description}
        </Typography.Text>
      )}

      <div className={styles.scanner_shell}>
        <QrCamera
          active={visible}
          facingMode={facingMode}
          onDecode={handleCameraDecode}
          onCameraError={handleCameraError}
        />

        <div className={styles.overlay_top}>
          <button
            type="button"
            className={styles.overlay_icon_btn}
            onClick={switchCamera}
            disabled={!cameraOk}
            aria-label="Switch camera"
            title="Switch camera"
          >
            <SwapOutlined />
          </button>
          <span
            className={`${styles.overlay_counter} ${styles.overlay_counter_collector}`}
          >
            {counterText}
          </span>
          <button
            type="button"
            className={styles.overlay_icon_btn}
            onClick={() => setMuted((m) => !m)}
            aria-label={muted ? 'Unmute scan feedback' : 'Mute scan feedback'}
            title={muted ? 'Sound off' : 'Sound on'}
          >
            {muted ? <SoundOutlined /> : <SoundFilled />}
          </button>
        </div>

        {flash && (
          <div
            key={`flash-${Date.now()}`}
            className={`${styles.flash} ${
              flash === 'success'
                ? styles.flash_success
                : styles.flash_duplicate
            }`}
          />
        )}

        {toast && (
          <div
            className={`${styles.toast} ${
              toast.kind === 'success'
                ? styles.toast_success
                : styles.toast_duplicate
            }`}
          >
            {toast.kind === 'success' && <CheckCircleFilled />}
            {toast.kind === 'duplicate' && <ExclamationCircleFilled />}
            <span>
              {toast.kind === 'success'
                ? `${toast.label} added`
                : `${toast.label} already added`}
            </span>
          </div>
        )}
      </div>

      <div className={styles.section_status}>
        {statusLine ? (
          <>
            <span className={styles.section_status_label}>Last:</span>
            <span style={{ fontFamily: 'monospace' }}>{statusLine.label}</span>
            {statusLine.kind === 'success' && (
              <Tag color="success" style={{ marginLeft: 'auto' }}>
                Added
              </Tag>
            )}
            {statusLine.kind === 'duplicate' && (
              <Tag color="warning" style={{ marginLeft: 'auto' }}>
                Already added
              </Tag>
            )}
          </>
        ) : (
          <span style={{ color: '#8c8c8c' }}>
            Scan a QR code to start building the filter.
          </span>
        )}
      </div>

      {/* Scanned list */}
      <button
        type="button"
        className={styles.collapsible_toggle}
        onClick={() => setShowList((v) => !v)}
      >
        <span className={styles.collapsible_toggle_label}>
          {showList ? <CaretDownOutlined /> : <CaretRightOutlined />}
          <strong>{scanned.length}</strong>{' '}
          {scanned.length === 1 ? noun.singular : noun.plural} in filter
        </span>
        {scanned.length > 0 && (
          <Button
            size="small"
            icon={<DeleteOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              setScanned([]);
              setStatusLine(null);
            }}
            style={{ color: '#ff4d4f', borderColor: '#ff4d4f' }}
          >
            Clear All
          </Button>
        )}
      </button>
      {showList && (
        <div className={styles.collapsible_body}>
          {scanned.length > 0 ? (
            <div className={styles.collector_tags_wrapper}>
              {scanned.map((l) => (
                <Tag
                  key={l}
                  closable
                  onClose={() =>
                    setScanned((prev) => prev.filter((x) => x !== l))
                  }
                  className={styles.collector_tag}
                >
                  {l}
                </Tag>
              ))}
            </div>
          ) : (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Nothing scanned yet.
            </Typography.Text>
          )}
        </div>
      )}

      {/* Manual entry */}
      <button
        type="button"
        className={styles.collapsible_toggle}
        onClick={() => setShowManual((v) => !v)}
      >
        <span className={styles.collapsible_toggle_label}>
          {showManual ? <CaretDownOutlined /> : <CaretRightOutlined />}
          <KeyOutlined /> Type manually
        </span>
      </button>
      {showManual && (
        <div className={styles.collapsible_body}>
          <div className={styles.manual_entry_row}>
            <Input
              placeholder={manualPlaceholder}
              value={manualInput}
              onChange={(e) => setManualInput(e.target.value)}
              onPressEnter={handleManualSubmit}
              allowClear
              autoFocus
            />
            <Button onClick={handleManualSubmit} disabled={!manualInput.trim()}>
              Add
            </Button>
          </div>
        </div>
      )}

      <div className={styles.footer}>
        <Button className={styles.footer_button} onClick={onCancel}>
          Cancel
        </Button>
        <Button
          className={styles.footer_button}
          type="primary"
          icon={<ScanOutlined />}
          disabled={scanned.length === 0}
          onClick={() => onApply(scanned)}
        >
          {applyButtonText} ({scanned.length})
        </Button>
      </div>
    </CustomModal>
  );
};

export default QrScanCollectorModal;
